import React, { createContext, useContext, useState, useEffect } from "react";
import { useTenantContext } from "./TenantContext";
import { integrationsApi } from "../services/api/integrations";

const IntegrationContext = createContext(null);

/**
 * Integration Context
 * Tracks which external systems (HR and POS/Till) are connected for the current tenant
 */
export const IntegrationProvider = ({ children }) => {
  const { selectedTenant, currentTenantId } = useTenantContext();
  const [integrations, setIntegrations] = useState({
    hr: null, // null | 'Sona' | 'BambooHR' | etc.
    pos: null, // null | 'Lightspeed' | 'Square' | etc.
    loading: false,
    error: null,
  });

  // Load integration status when tenant changes
  useEffect(() => {
    if (currentTenantId) {
      loadIntegrations(currentTenantId);
    } else {
      setIntegrations({
        hr: null,
        pos: null,
        loading: false,
        error: null,
      });
    }
  }, [currentTenantId]);

  const loadIntegrations = async (tenantId) => {
    setIntegrations((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Don't pass tenantId as query param - it's already in X-Tenant-Id header
      const response = await integrationsApi.getStatus();

      if (response.success) {
        const data = response.data;
        setIntegrations({
          hr: data.hrProvider,
          pos: data.posProvider,
          loading: false,
          error: null,
        });
      } else {
        // Don't block the app on error - just assume no integrations
        console.warn("Could not load integration status:", response.message);
        setIntegrations({
          hr: null,
          pos: null,
          loading: false,
          error: null, // Don't expose error to prevent blocking
        });
      }
    } catch (error) {
      // Don't block the app on error - just assume no integrations
      console.error("Error loading integrations:", error);
      setIntegrations({
        hr: null,
        pos: null,
        loading: false,
        error: null, // Don't expose error to prevent blocking
      });
    }
  };

  const refreshIntegrations = async () => {
    if (currentTenantId) {
      await loadIntegrations(currentTenantId);
    }
  };

  // Feature flags based on integrations
  const hasHrIntegration = integrations.hr !== null;
  const hasPosIntegration = integrations.pos !== null;

  // Specific feature permissions
  const canEditEmployeeDetails = !hasHrIntegration; // Only if no HR system
  const canEditJobRoles = !hasHrIntegration; // Only if no HR system
  const canEditEmployeeLocation = !hasHrIntegration; // Only if no HR system
  const canAddClockRecords = !hasHrIntegration; // Only if no HR system (manual entry)
  const canEditClockRecords = !hasHrIntegration; // Only if no HR system
  const canAddTillData = !hasPosIntegration; // Only if no POS system (manual entry)

  // Data source indicators
  const employeeDataSource = hasHrIntegration ? integrations.hr : "Manual";
  const clockDataSource = hasHrIntegration ? integrations.hr : "Manual";
  const tillDataSource = hasPosIntegration ? integrations.pos : "Manual";

  const value = {
    // Integration status
    integrations,
    hasHrIntegration,
    hasPosIntegration,
    loading: integrations.loading,
    error: integrations.error,

    // Feature permissions
    canEditEmployeeDetails,
    canEditJobRoles,
    canEditEmployeeLocation,
    canAddClockRecords,
    canEditClockRecords,
    canAddTillData,

    // Data sources
    employeeDataSource,
    clockDataSource,
    tillDataSource,

    // Actions
    refreshIntegrations,
  };

  return (
    <IntegrationContext.Provider value={value}>
      {children}
    </IntegrationContext.Provider>
  );
};

/**
 * Hook to access integration context
 */
export const useIntegrations = () => {
  const context = useContext(IntegrationContext);
  if (!context) {
    throw new Error(
      "useIntegrations must be used within an IntegrationProvider",
    );
  }
  return context;
};

export default IntegrationContext;
